import { useCartStore } from "../../../../hooks/stores/useCartStore";
import { Drawer } from "../../../../components/common/drawer";
import { Text, Heading } from "../../../../components/common/typography";
import { Image } from "../../../../components/common/image";
import { CurrencyAmount } from "../../../../components/common/currency_amount";
import { QuantitySelector } from "../../../../components/forms/quantity-selector";
import { Button } from "../../../../components/common/button";
import { Badge } from "../../../../components/common/badge";
import { Link, useNavigate } from "react-router-dom";
import { AppRoutes } from "../../../../common/routes/app_routes";
import { IconShoppingCart } from "@tabler/icons-react";
import { EmptyState } from "../../../../components/common/empty-state";
import { useCreateQuotation } from "../../hooks/useCreateQuotation";
import type { QuotationOrderLineDto } from "../../../../api";
import { getProductLineTotal } from "../../../products/utils/get-pricelist-price";
import { getProductDetailsUrl } from "../../../products/utils/get-product-details-url";
import { IconTrash } from "@tabler/icons-react";
interface CartDrawerProps {
    open: boolean;
    onClose: () => void;
}
export function CartDrawer({ open, onClose }: CartDrawerProps) {
    const navigate = useNavigate();
    const { items, updateQuantity, removeFromCart, clearCart, getTotalItems, getTotalPrice, } = useCartStore();
    const { createQuotation, isLoading: isCreatingQuotation } = useCreateQuotation({ clearCartOnSuccess: true });
    const totalItems = getTotalItems();
    const totalPrice = getTotalPrice();
    const handleQuantityChange = (productId: number, newQuantity: number) => {
        updateQuantity(productId, newQuantity);
    };
    const handleRemoveItem = (productId: number) => {
        removeFromCart(productId);
    };
    const handleClearCart = () => {
        clearCart();
    };
    const handleCheckout = () => {
        onClose();
        navigate(AppRoutes.CHECKOUT);
    };
    const handleCreateQuotation = async () => {
        // Convert cart items to quotation order lines
        const orderLines: QuotationOrderLineDto[] = items.map((item) => {
            return {
                productId: item.product.id,
                quantity: item.quantity,
            };
        });
        const quotation = await createQuotation(orderLines);
        if (quotation) {
            // Optionally close the drawer or navigate to quotation details
            onClose();
        }
    };
    if (items.length === 0) {
        return (<Drawer open={open} close={onClose} title="Shopping Cart" content={
          <div className="flex h-full min-h-[400px] items-center justify-center">
            <EmptyState
              icon={IconShoppingCart}
              title="Your cart is empty"
              primaryAction={{
                label: "Browse Products",
                onClick: () => {
                  onClose();
                  navigate(AppRoutes.PRODUCTS);
                },
              }}
            />
          </div>
        }/>);
    }
    return (<Drawer open={open} close={onClose} title="Shopping Cart" content={<div className="flex flex-col h-full">
          {/* Cart Items */}
          <div className="flex-1 overflow-y-auto">
            <div className="flex flex-col gap-4">
              {items.map((item) => {
                const availableQuantity = Number(item.product.supplierStock ?? 0);
                const isOutOfStock = availableQuantity === 0;
                const isLowStock = availableQuantity > 0 && availableQuantity < 20;
                const detailsUrl = getProductDetailsUrl(
                  item.product.productTemplateId ?? item.product.id,
                  item.product.id,
                );
                return (<div key={item.product.id} className="flex gap-4 p-4 bg-uiSecondary rounded-lg">
                    {/* Product Image */}
                    <Link to={detailsUrl} onClick={onClose} aria-label={`View ${item.product.name ?? "product"}`} className="w-20 h-20 flex-shrink-0 hover:opacity-90 transition-opacity">
                      <Image image={item.product.image} alt={item.product.name || "Product"} size="w-full h-full object-cover rounded"/>
                    </Link>

                    {/* Product Details */}
                    <div className="flex-1 flex flex-col gap-2">
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1">
                          <Link to={detailsUrl} onClick={onClose} className="hover:text-brandPrimary transition-colors">
                            <Text variant="body" tone="dark" className="mb-1">
                              {item.product.name || "—"}
                            </Text>
                          </Link>
                          {item.product.category?.name && (<Text variant="bodyXs" tone="body">
                              {item.product.category.name}
                            </Text>)}
                        </div>
                        <button onClick={() => handleRemoveItem(item.product.id)} className="p-1 hover:bg-uiSecondaryDisabled rounded transition-colors flex-shrink-0" aria-label="Remove item">
                          <IconTrash size={20} style={{ color: "#E34D5C" }}/>
                        </button>
                      </div>

                      {/* Price and Quantity */}
                      <div className="flex items-center justify-between gap-4">
                        <CurrencyAmount amount={getProductLineTotal(item.product, item.quantity)} currency="QAR" variant="bodyLg" tone="dark"/>
                        <div className="flex items-center gap-2">
                          <QuantitySelector value={item.quantity} onChange={(newQty) => handleQuantityChange(item.product.id, newQty)} min={1} max={availableQuantity > 0
                        ? availableQuantity
                        : undefined} disabled={isOutOfStock}/>
                          {isLowStock && (<Badge label="Low Stock" state="pending"/>)}
                          {isOutOfStock && (<Badge label="Out of Stock" state="error"/>)}
                        </div>
                      </div>
                    </div>
                  </div>);
            })}
            </div>
          </div>

          {/* Cart Summary */}
          <div className="border-t border-uiSecondary pt-4 mt-4">
            <div className="flex items-center justify-between mb-4">
              <Text variant="bodyLgBold" tone="dark">
                Total Items:
              </Text>
              <Text variant="bodyLgBold" tone="dark">
                {totalItems}
              </Text>
            </div>
            <div className="flex items-center justify-between mb-6">
              <Heading variant="h3" tone="dark">
                Total:
              </Heading>
              <CurrencyAmount amount={totalPrice} currency="QAR" variant="h3" tone="dark"/>
            </div>

            {/* Actions */}
            <div className="flex flex-col gap-3">
              <Button variant="secondary" onClick={handleCreateQuotation} className="w-full" disabled={isCreatingQuotation}>
                {isCreatingQuotation
                ? "Creating Quotation..."
                : "Create Quotation"}
              </Button>
              <Button variant="primary" onClick={handleCheckout} className="w-full">
                Proceed to Checkout
              </Button>
              <button onClick={handleClearCart} className="w-full px-4 py-2 border border-uiSecondary rounded-lg text-primary hover:bg-uiSecondary transition-colors">
                <Text variant="body" tone="dark">
                  Clear Cart
                </Text>
              </button>
            </div>
          </div>
        </div>}/>);
}
